import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Send, Mail, KeyRound, CheckCircle2, AlertTriangle, PlayCircle } from "lucide-react";
import { AppLayout, PageHeader } from "@/components/AppLayout";
import { DataTable, Toolbar, Btn, Field, Input, Select, Textarea, Modal, ConfirmDialog } from "@/components/Primitives";
import { api, formatApiErrorDetail, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const emptySchedule = () => ({
  name: "", recipients: "", send_time: "07:00", frequency: "daily",
  weekdays: [], day_of_month: 1, reports: [], subject: "", body: "", active: true,
});

const freqSummary = (s) => {
  if (s.frequency === "weekly") return `${(s.weekdays || []).map((w) => WEEKDAYS[w]).join(", ")} @ ${s.send_time}`;
  if (s.frequency === "monthly") return `Day ${s.day_of_month} of month @ ${s.send_time}`;
  return `Daily @ ${s.send_time}`;
};

export default function EmailCenter() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [schedules, setSchedules] = useState([]);
  const [reports, setReports] = useState([]);
  const [log, setLog] = useState([]);
  const [secret, setSecret] = useState(null);
  const [sched, setSched] = useState(null);
  const [editing, setEditing] = useState(null);
  const [delItem, setDelItem] = useState(null);
  const [running, setRunning] = useState(null);

  const load = () => {
    api.get("/mailer/schedules").then((r) => setSchedules(r.data)).catch(() => {});
    api.get("/mailer/log").then((r) => setLog(r.data)).catch(() => {});
    api.get("/mailer/secret-status").then((r) => setSecret(r.data)).catch(() => {});
    api.get("/mailer/scheduler-status").then((r) => setSched(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    api.get("/mailer/reports").then((r) => setReports(r.data)).catch(() => {});
  }, []);

  // While a manual send is running in the background, poll until it finishes.
  const anyRunning = schedules.some((s) => s.last_run?.status === "running");
  useEffect(() => {
    if (!anyRunning) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [anyRunning]);
  useEffect(() => {
    if (!running) return;
    const s = schedules.find((x) => x.id === running);
    if (!s || s.last_run?.status === "running") return;
    setRunning(null);
    if (s.last_run?.status === "sent") toast.success(`"${s.name}" sent to ${s.recipients.join(", ")}`);
    else toast.error(`"${s.name}" failed: ${s.last_run?.error || "unknown error"}`);
  }, [schedules, running]);

  const toggleActive = async (s) => {
    try {
      await api.put(`/mailer/schedules/${s.id}`, { ...s, active: !s.active });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const runNow = async (s) => {
    setRunning(s.id);
    try {
      await api.post(`/mailer/schedules/${s.id}/run-now`);
      toast.info(`Sending "${s.name}" in the background — building the reports can take a few minutes.`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); setRunning(null); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/mailer/schedules/${delItem.id}`);
      toast.success("Schedule deleted");
      setDelItem(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <AppLayout>
      <PageHeader
        title="Email Center"
        subtitle="Automatic report emails, send history and mail credential status. Times are South African (SAST)."
        actions={isAdmin && (
          <Btn onClick={() => setEditing(emptySchedule())} testid="new-schedule-btn">
            <Plus className="h-4 w-4" /> New Scheduled Email
          </Btn>
        )}
      />

      {secret && <SecretCard secret={secret} isAdmin={isAdmin} onSaved={load} />}
      {sched && <SchedulerStatus sched={sched} />}

      <SignatureCard isAdmin={isAdmin} />

      <div className="font-display text-lg font-semibold mt-6 mb-2">Scheduled Emails</div>
      {schedules.length === 0 ? (
        <div className="panel p-6 text-sm text-stone-500" data-testid="no-schedules">
          No scheduled emails yet. Create one to automatically send reports to any mailbox daily, weekly or monthly.
        </div>
      ) : (
        <DataTable
          testid="schedules-table"
          columns={[
            { header: "Name", render: (s) => <span className="font-semibold">{s.name}</span> },
            { header: "Recipients", render: (s) => <span className="text-xs">{(s.recipients || []).join(", ")}</span> },
            { header: "When", mono: true, render: (s) => <span className="text-xs">{freqSummary(s)}</span> },
            { header: "Reports", render: (s) => (
              <span className="text-xs text-stone-600" title={(s.reports || []).join(", ")}>
                {(s.reports || []).length} report{(s.reports || []).length === 1 ? "" : "s"}
              </span>
            )},
            { header: "Active", render: (s) => (
              <button onClick={() => isAdmin && toggleActive(s)} disabled={!isAdmin}
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.active ? "bg-emerald-100 text-emerald-800" : "bg-stone-200 text-stone-500"}`}
                      data-testid={`schedule-toggle-${s.id}`}>
                {s.active ? "Active" : "Paused"}
              </button>
            )},
            { header: "Last Run", render: (s) => s.last_run ? (
              <div className="text-xs" data-testid={`schedule-last-run-${s.id}`}>
                <span className={s.last_run.status === "sent" ? "text-emerald-700" : s.last_run.status === "running" ? "text-amber-700 font-semibold animate-pulse" : "text-red-700 font-semibold"}
                      title={s.last_run.error || ""}>
                  {s.last_run.status === "sent" ? "Sent" : s.last_run.status === "running" ? "Sending…" : "Failed"}
                </span>
                <span className="text-stone-500"> · {fmtDate(s.last_run.at?.slice(0, 10))} {s.last_run.at?.slice(11, 16)}</span>
              </div>
            ) : <span className="text-xs text-stone-400">—</span> },
            { header: "Actions", align: "right", render: (s) => isAdmin && (
              <div className="flex items-center gap-2 justify-end">
                <button onClick={() => runNow(s)} disabled={running === s.id}
                        className="text-emerald-800 hover:text-emerald-900 disabled:opacity-40"
                        title="Send now" data-testid={`schedule-run-${s.id}`}>
                  <PlayCircle className="h-4 w-4" />
                </button>
                <button onClick={() => setEditing({ ...s, recipients: (s.recipients || []).join(", ") })}
                        className="text-stone-500 hover:text-emerald-800" title="Edit" data-testid={`schedule-edit-${s.id}`}>
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => setDelItem(s)} className="text-stone-500 hover:text-red-700"
                        title="Delete" data-testid={`schedule-delete-${s.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            )},
          ]}
          rows={schedules}
        />
      )}

      <div className="font-display text-lg font-semibold mt-8 mb-2">Send Log</div>
      {log.length === 0 ? (
        <div className="panel p-6 text-sm text-stone-500" data-testid="no-log">No emails sent yet.</div>
      ) : (
        <DataTable
          testid="email-log-table"
          columns={[
            { header: "Time", mono: true, render: (r) => <span className="text-xs">{fmtDate(r.sent_at?.slice(0, 10))} {r.sent_at?.slice(11, 16)}</span> },
            { header: "Type", render: (r) => <span className="text-xs uppercase tracking-wider px-2 py-0.5 rounded-sm bg-stone-100 text-stone-700">{r.type}</span> },
            { header: "To", render: (r) => <span className="text-xs">{r.to}</span> },
            { header: "Subject", render: (r) => <span className="text-xs">{r.subject}</span> },
            { header: "Attachments", align: "right", mono: true, render: (r) => (r.attachments || []).length },
            { header: "Status", render: (r) => (
              <span className={`text-xs font-medium ${r.status === "sent" ? "text-emerald-700" : "text-red-700"}`}>
                {r.status === "sent" ? "Sent" : "Failed"}
              </span>
            )},
            { header: "Error", render: (r) => r.error ? <span className="text-xs text-red-700 whitespace-normal break-words block max-w-[360px]">{r.error}</span> : "—" },
          ]}
          rows={log}
        />
      )}

      {editing && (
        <ScheduleModal schedule={editing} reports={reports}
                       onClose={() => setEditing(null)}
                       onSaved={() => { setEditing(null); load(); }} />
      )}

      <ConfirmDialog
        open={!!delItem}
        onClose={() => setDelItem(null)}
        onConfirm={doDelete}
        title="Delete Scheduled Email"
        confirmLabel="Delete"
        testid="schedule-delete-confirm"
        message={delItem ? `Delete the scheduled email "${delItem.name}"? It will no longer be sent.` : ""}
      />
    </AppLayout>
  );
}

function SchedulerStatus({ sched }) {
  const bad = !sched.alive || !sched.configured;
  const tick = sched.last_tick ? new Date(sched.last_tick).toLocaleString("en-ZA", { timeZone: "Africa/Johannesburg" }) : "never";
  return (
    <div className={`panel px-4 py-2 mt-3 flex flex-wrap items-center gap-3 text-xs ${bad ? "border-amber-300 bg-amber-50" : ""}`}
         data-testid="scheduler-status">
      {bad ? <AlertTriangle className="h-4 w-4 text-amber-600" /> : <CheckCircle2 className="h-4 w-4 text-emerald-700" />}
      <span className="font-semibold">Scheduler:</span>
      <span data-testid="scheduler-alive">{sched.alive ? "running" : "not running"}</span>
      <span className="text-stone-500">· last check {tick} (SAST)</span>
      {!sched.configured && <span className="text-amber-800 font-medium" data-testid="scheduler-not-configured">· Mail credentials missing on this server — scheduled emails will fail until set</span>}
      {sched.error && <span className="text-red-700" data-testid="scheduler-error">· {sched.error}</span>}
    </div>
  );
}

function SecretCard({ secret, isAdmin, onSaved }) {
  const [editDate, setEditDate] = useState(false);
  const [val, setVal] = useState(secret.expiry);
  const level = secret.level;

  const save = async () => {
    try {
      await api.put("/mailer/secret-expiry", { expiry: val });
      toast.success("Expiry date updated");
      setEditDate(false);
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className={`panel p-4 flex flex-wrap items-center gap-4 ${level === "expired" ? "border-red-300 bg-red-50" : level === "warning" ? "border-amber-300 bg-amber-50" : ""}`}
         data-testid="secret-card">
      <KeyRound className={`h-6 w-6 ${level === "ok" ? "text-emerald-700" : level === "warning" ? "text-amber-600" : "text-red-600"}`} />
      <div className="flex-1 min-w-[260px]">
        <div className="text-sm font-semibold flex items-center gap-2">
          Microsoft 365 mail credential
          {level === "ok" ? <CheckCircle2 className="h-4 w-4 text-emerald-700" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
        </div>
        <div className="text-xs text-stone-600 mt-0.5">
          Sending from <span className="font-medium">{secret.mailbox}</span> · Client secret expires{" "}
          <span className="font-mono font-semibold" data-testid="secret-expiry">{secret.expiry}</span>{" "}
          ({secret.days_left >= 0 ? `${secret.days_left} days left` : `expired ${-secret.days_left} days ago`})
        </div>
        {level !== "ok" && (
          <div className="text-xs mt-1 text-stone-700">
            {level === "expired" ? "Emails will fail until renewed. " : "Renew soon to avoid interruption. "}
            Go to entra.microsoft.com → App registrations → Forestry WMS Mailer → Certificates &amp; secrets → New client secret,
            then send the new secret value to your developer and update the expiry date here.
          </div>
        )}
      </div>
      {isAdmin && (editDate ? (
        <div className="flex items-center gap-2">
          <Input type="date" value={val} onChange={(e) => setVal(e.target.value)} className="w-40" data-testid="secret-expiry-input" />
          <Btn onClick={save} testid="secret-expiry-save">Save</Btn>
          <Btn variant="secondary" onClick={() => setEditDate(false)}>Cancel</Btn>
        </div>
      ) : (
        <button onClick={() => setEditDate(true)} className="text-xs text-emerald-800 hover:underline" data-testid="secret-expiry-edit">
          Edit expiry date
        </button>
      ))}
    </div>
  );
}

function SignatureCard({ isAdmin }) {
  const [sig, setSig] = useState(null);
  const [editing, setEditing] = useState(false);
  const fileRef = React.useRef(null);
  const pasteRef = React.useRef(null);

  const loadSig = () => api.get("/mailer/signature").then((r) => setSig(r.data)).catch(() => {});
  useEffect(() => { loadSig(); }, []);

  // Convert blob: images to data URLs and drop unreachable file:/cid: images after paste.
  const cleanPasted = async () => {
    const box = pasteRef.current;
    if (!box) return;
    let dropped = 0;
    for (const img of Array.from(box.querySelectorAll("img"))) {
      const src = img.getAttribute("src") || "";
      if (src.startsWith("blob:")) {
        try {
          const blob = await (await fetch(src)).blob();
          const dataUrl = await new Promise((res) => {
            const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(blob);
          });
          img.setAttribute("src", dataUrl);
        } catch { img.remove(); dropped += 1; }
      } else if (src.startsWith("file:") || src.startsWith("cid:")) {
        img.remove(); dropped += 1;
      }
    }
    if (dropped) toast.warning(`${dropped} image(s) could not be read from the clipboard — try copying the signature from a sent email in Outlook on the web instead`);
  };

  const savePasted = async () => {
    const html = pasteRef.current?.innerHTML?.trim();
    if (!html || html === "<br>") { toast.error("Paste your signature into the box first"); return; }
    try {
      await api.put("/mailer/signature", { html });
      toast.success("Signature saved — it will appear at the bottom of every email");
      setEditing(false);
      loadSig();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1_500_000) { toast.error("Image too large — keep it under 1.5 MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.put("/mailer/signature", { data_url: reader.result });
        toast.success("Signature saved");
        setEditing(false);
        loadSig();
      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    };
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  const remove = async () => {
    try {
      await api.delete("/mailer/signature");
      toast.success("Signature removed");
      loadSig();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  if (!sig) return null;
  return (
    <div className="panel p-4 mt-3" data-testid="signature-card">
      <div className="flex flex-wrap items-center gap-4">
        <Mail className="h-6 w-6 text-emerald-700" />
        <div className="flex-1 min-w-[260px]">
          <div className="text-sm font-semibold">Email signature</div>
          <div className="text-xs text-stone-600 mt-0.5">
            {sig.has
              ? "Added to the bottom of every email the system sends (scheduled reports and invoice emails)."
              : "Copy your signature in Outlook (select it in a new email or a sent mail, Ctrl+C), then paste it here exactly as it looks."}
          </div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={onFile} data-testid="signature-file-input" />
            {!editing && (
              <Btn variant={sig.has ? "secondary" : undefined} onClick={() => setEditing(true)} testid="signature-paste-btn">
                {sig.has ? "Replace" : "Paste Signature"}
              </Btn>
            )}
            {sig.has && !editing && <Btn variant="secondary" onClick={remove} testid="signature-remove-btn">Remove</Btn>}
          </div>
        )}
      </div>

      {sig.has && !editing && (
        <div className="mt-3 border border-stone-200 rounded-sm bg-white p-3 max-w-[560px] overflow-auto" data-testid="signature-preview">
          {sig.type === "html"
            ? <div dangerouslySetInnerHTML={{ __html: sig.html }} />
            : <img src={sig.data_url} alt="Email signature" className="max-h-40" />}
        </div>
      )}

      {editing && (
        <div className="mt-3 space-y-2">
          <div
            ref={pasteRef}
            contentEditable
            suppressContentEditableWarning
            onPaste={() => setTimeout(cleanPasted, 100)}
            className="min-h-[110px] border-2 border-dashed border-emerald-400 rounded-sm bg-white p-3 text-sm focus:outline-none focus:border-emerald-600"
            data-testid="signature-paste-box"
          />
          <div className="text-xs text-stone-500">
            Click inside the box and press <b>Ctrl+V</b> to paste your Outlook signature — pictures, formatting and links come with it, exactly at the size you see above.
            If the pictures don't come through, use <button className="underline text-emerald-800" onClick={() => fileRef.current?.click()}>upload an image</button> instead.
          </div>
          <div className="flex gap-2">
            <Btn onClick={savePasted} testid="signature-save-btn">Save Signature</Btn>
            <Btn variant="secondary" onClick={() => setEditing(false)}>Cancel</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

function ScheduleModal({ schedule, reports, onClose, onSaved }) {
  const [form, setForm] = useState(schedule);
  const [saving, setSaving] = useState(false);
  const isNew = !form.id;

  const toggleReport = (key) => setForm((f) => ({
    ...f, reports: f.reports.includes(key) ? f.reports.filter((k) => k !== key) : [...f.reports, key],
  }));
  const toggleWeekday = (w) => setForm((f) => ({
    ...f, weekdays: f.weekdays.includes(w) ? f.weekdays.filter((x) => x !== w) : [...f.weekdays, w],
  }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, recipients: form.recipients };
      if (isNew) await api.post("/mailer/schedules", payload);
      else await api.put(`/mailer/schedules/${form.id}`, payload);
      toast.success(isNew ? "Schedule created" : "Schedule updated");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? "New Scheduled Email" : `Edit · ${form.name}`} size="lg" testid="schedule-modal"
           footer={<><Btn variant="secondary" onClick={onClose}>Cancel</Btn>
             <Btn onClick={save} disabled={saving} testid="schedule-save">{saving ? "Saving..." : "Save Schedule"}</Btn></>}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Name" required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="e.g. Morning stock report" data-testid="schedule-name" />
          </Field>
          <Field label="Recipients (comma separated)" required>
            <Input value={form.recipients} onChange={(e) => setForm({ ...form, recipients: e.target.value })}
                   placeholder="ops@company.com, boss@company.com" data-testid="schedule-recipients" />
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Frequency" required>
            <Select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} data-testid="schedule-frequency">
              <option value="daily">Daily</option>
              <option value="weekly">Weekly (pick days)</option>
              <option value="monthly">Monthly (pick date)</option>
            </Select>
          </Field>
          <Field label="Send Time (SAST)" required>
            <Input type="time" value={form.send_time} onChange={(e) => setForm({ ...form, send_time: e.target.value })} data-testid="schedule-time" />
          </Field>
          {form.frequency === "monthly" && (
            <Field label="Day of Month" required>
              <Input type="number" min="1" max="31" value={form.day_of_month}
                     onChange={(e) => setForm({ ...form, day_of_month: e.target.value })} data-testid="schedule-dom" />
            </Field>
          )}
        </div>

        {form.frequency === "weekly" && (
          <Field label="Days" required>
            <div className="flex gap-1.5 flex-wrap" data-testid="schedule-weekdays">
              {WEEKDAYS.map((d, i) => (
                <button key={d} type="button" onClick={() => toggleWeekday(i)}
                        className={`px-3 py-1.5 text-xs rounded-sm border font-medium ${form.weekdays.includes(i)
                          ? "bg-emerald-800 text-white border-emerald-800"
                          : "bg-white text-stone-600 border-stone-300 hover:border-emerald-700"}`}
                        data-testid={`weekday-${d.toLowerCase()}`}>
                  {d}
                </button>
              ))}
            </div>
          </Field>
        )}

        <Field label="Reports to attach" required>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="schedule-reports">
            {reports.map((r) => (
              <label key={r.key} className={`flex items-center gap-2 px-3 py-2 rounded-sm border text-sm cursor-pointer ${form.reports.includes(r.key)
                ? "border-emerald-700 bg-emerald-50" : "border-stone-200 hover:border-stone-400"}`}>
                <input type="checkbox" checked={form.reports.includes(r.key)} onChange={() => toggleReport(r.key)}
                       data-testid={`report-check-${r.key}`} />
                {r.label}
              </label>
            ))}
          </div>
        </Field>

        <div className="grid grid-cols-1 gap-4">
          <Field label="Subject (defaults to name)">
            <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="schedule-subject" />
          </Field>
          <Field label="Message">
            <Textarea rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                      placeholder="Please find the attached reports." data-testid="schedule-body" />
          </Field>
        </div>

        <div className="text-xs text-stone-500 flex items-center gap-1.5">
          <Mail className="h-3.5 w-3.5" />
          Fresh copies of the selected reports are generated and attached at send time.
          If the send time has already passed today, the first email goes out on the next matching day.
        </div>
      </div>
    </Modal>
  );
}
